import React, { useState } from "react"
import {formatDateTime, titleize} from '../../utils'

import { compose } from "redux"
import { connect } from 'react-redux'
import { customers, flashMessages, modals, schoolYears } from "ducks"
import duckify, { singleDuck } from "../../utils/duckify"

import Placeholder from "../Placeholder";
import ModalRoot from "../ModalRoot"
import ModalComponent from "../ModalComponent"
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import LinkButton from "../LinkButton/LinkButton";
import CustomGradeConfiguration from "./CustomGradeConfiguration"
import CustomGradeInputRow from "./CustomGradeInputRow"

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons/faTrashAlt'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle'

import './styles/GradesSchoolYear.scss'

const SchoolYearConfigPage = ({ allDucksReady, onDelete, onSubmit, schoolYear, customer }) => {
  const globalSetting = schoolYear ? { ...schoolYear } : {}
  const [isEditing, setIsEditing] = useState(false)
  globalSetting.auto_open_date = globalSetting?.auto_open_date ? formatDateTime(globalSetting.auto_open_date, 'fns_date_time_minutes') : ''
  globalSetting.auto_close_date = globalSetting?.auto_close_date ? formatDateTime(globalSetting.auto_close_date, 'fns_date_time_minutes') : ''
  const status = globalSetting.global_dates ?
    <span className="grade-status">
      <FontAwesomeIcon className="collecting" icon={faCheckCircle} /> Enabled
    </span> :
    <span className="grade-status">
      <FontAwesomeIcon className="not-collecting" icon={faBan} /> Disabled
    </span>

  const handleDelete = () => {
    schoolYear.global_dates = false
    schoolYear.auto_open_date = null
    schoolYear.auto_close_date = null
    schoolYear.auto_collect = false
    onDelete(schoolYear)
  }

  const appTerminology = customer && customer.admin_admissions_terms['noun'];
  const applicantsTerminology = `${customer && customer.admin_admissions_terms['person']}s`;

  return (
    <div className="school-year-configuration">
      <ModalRoot modals={{ ConfirmRemoveGlobalDates }} />
      <fieldset>
        {allDucksReady &&
          <div className="school-year-config-page">
            <form>
              <div className="school-year-form-horizontal">
                <legend className="global-dates-legend">
                  Global Configuration For All Grades
                  <OverlayTrigger
                    placement={'top'}
                    overlay={
                      <Tooltip id="global-config">
                        Configure global settings for all grades.  Set the open and close dates for the term.  Additionally, define the action to be triggered upon reaching the closing date.
                      </Tooltip>
                    }
                  >
                    <span>
                      <FontAwesomeIcon className="tooltip-icon fa-sm" icon={faInfoCircle} />
                    </span>
                  </OverlayTrigger>
                </legend>

                <Placeholder.loading ready={allDucksReady}>
                  <table className="table global-auto-dates-list">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Open Date</th>
                        <th>Close Date</th>
                        <th></th>
                        <th>Upon Close Date/Max {titleize(applicantsTerminology)}</th>
                        <th></th>
                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className={globalSetting.global_dates ? 'selected' : 'not'}>
                        <td>All Grades</td>
                        <td>{globalSetting.auto_open_date}</td>
                        <td>{globalSetting.auto_close_date}</td>
                        <td></td>
                        <td>{globalSetting.auto_collect ? 'Collect and auto-waitlist applications' : `Don't collect ${appTerminology}s`}</td>
                        <td></td>
                        <td className="grade-school-year-actions">
                          {!isEditing ?
                            <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>Edit</Button> : ''}
                          {!isEditing && globalSetting.global_dates ?
                            <LinkButton
                              content={<FontAwesomeIcon icon={faTrashAlt} className="fa-lg hover-button red" />}
                              onClick={handleDelete}
                            /> : ''
                          }
                        </td>
                      </tr>
                      {isEditing &&
                        <CustomGradeInputRow
                          gradeSchoolYear={globalSetting}
                          globalDatesConfig={true}
                          onSubmit={onSubmit}
                          setIsEditing={setIsEditing}
                        />
                      }
                    </tbody>
                  </table>
                </Placeholder.loading>
              </div>
            </form>
            <CustomGradeConfiguration schoolYear={schoolYear} />
          </div>
        }
      </fieldset>
    </div>
  )
}

const ConfirmRemoveGlobalDates = connect()(({ dispatch, deleting = false, schoolYear }) => (
  <ModalComponent
    title="Confirm Remove Global Dates"
    buttons={[
      <button className="btn btn-danger"
        disabled={deleting}
        onClick={() => {
          dispatch(modals.actions.update({ deleting: true }))
          dispatch(schoolYears.actions.save(schoolYear))
            .then(() => dispatch(modals.actions.hide()))
            .then(() => dispatch(schoolYears.actions.markDirty()))
            .then(() => dispatch(flashMessages.actions.show({
              type: 'success',
              message: `Global auto date configurations have been removed successfully.`,
              dismissible: true
            })))
        }}
      >
        {deleting ? 'Deleting...' : 'Delete'}
      </button>
    ]}
    closeText="Cancel"
  >
    <p>Are you sure you want to delete the global auto date configurations?</p>
  </ModalComponent>
)
)

const mapDucksToProps = () => ({
  customer: singleDuck(customers, 'current')
})

const mapDispatchToProps = (dispatch) => ({
  onDelete: (schoolYear) => dispatch(modals.actions.show('ConfirmRemoveGlobalDates', { schoolYear })),
  onSubmit: (schoolYear, { setErrors, setIsEditing }) => {
    // auto_collect false by default.  If user hits edit and save without making any changes we don't set global_dates.
    // If something is changed and then user deletes 'resets' settings manually (not hitting the delete button), we will still consider global_dates as true.
    if (schoolYear.auto_collect || schoolYear.auto_open_date || schoolYear.auto_close_date) {
      schoolYear.global_dates = true
    }
    return (dispatch(schoolYears.actions.save(schoolYear)))
      .then(() => dispatch(schoolYears.actions.markDirty()))
      .then(() => setIsEditing(false))
      .catch(err => {
        const { response, ...cleanErrors } = err
        setErrors(cleanErrors)
      })
  }
})

export default compose(
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps)
)(SchoolYearConfigPage)
